/**
 * Wire contract between the Worker API and the React client.
 *
 * These types describe what actually crosses HTTP. Worker-internal domain
 * objects, database rows and third party payloads are defined next to the code
 * that owns them, never here.
 */

import type { ActivityKey } from './activities'
import type {
  AnchorKind,
  AnchorSource,
  ReferenceDatasetKey,
  ReferenceDatasetStatus,
} from './anchors'
import type { DimensionScores } from './dimensions'
import type { CityKey } from './cities'
import type {
  AdminCapability,
  AdminRole,
  AdminStatus,
  ApplicationStatus,
  MailStatus,
  MapCallsMode,
  RecommendationTriggerSource,
  ReferralChannel,
} from './constants'
import type { ApiErrorCode } from './errors'
import type { Locale } from './locales'
import type { TravelMode } from './travel'

/* ------------------------------------------------------------------ */
/* Auth                                                                */
/* ------------------------------------------------------------------ */

export interface UserDto {
  id: string
  email: string
  nickname: string | null
  avatarUrl: string | null
  locale: Locale
  isInvited: boolean
  invitedAt: number | null
  /** Public identifier this account hands out in share and promotion links. */
  referralCode: string
  /** True when an enabled administrator record points at this account. */
  isAdmin: boolean
  /** Effective capabilities, already resolved against the role preset. */
  capabilities: AdminCapability[]
  createdAt: number
}

export interface RequestVerificationCodeResponse {
  ok: true
  /** Seconds the client must wait before requesting another code. */
  resendAfterSeconds: number
  /** Remaining sends for the current day. */
  remainingToday: number
  /**
   * Present only outside production: the Worker cannot deliver real mail in
   * local development, so the code is handed back explicitly.
   */
  developmentCode?: string
  /**
   * The account this request signed in, in a local development deployment.
   *
   * There is no readable mailbox there, so no code is produced at all: the
   * request that would have sent one returns a session cookie and this account
   * instead, and the client skips the code step entirely.
   */
  user?: UserDto
}

/**
 * Successful sign in.
 *
 * The session travels in an `HttpOnly` cookie, so no credential crosses this
 * body: the client only learns who it is now signed in as.
 */
export interface SignInResponse {
  user: UserDto
}

export interface CurrentUserResponse {
  user: UserDto
}

/* ------------------------------------------------------------------ */
/* Invites                                                             */
/* ------------------------------------------------------------------ */

/** The applicant's own view of their most recent request. */
export interface InviteApplicationDto {
  id: string
  status: ApplicationStatus
  reason: string | null
  /** Set once a decision was taken; the rejection reason is shown to the applicant. */
  decisionNote: string | null
  createdAt: number
  decidedAt: number | null
}

export interface InviteStatusResponse {
  isInvited: boolean
  invitedAt: number | null
  isAdmin: boolean
  /** `isInvited || isAdmin`, the gate the client renders the create link behind. */
  canCreateEvent: boolean
  application: InviteApplicationDto | null
  canApply: boolean
  /** When the current rejection cooldown ends, `null` when applying is allowed. */
  cooldownEndsAt: number | null
  /** Seconds to wait before the invite mail may be sent again. */
  resendAfterSeconds: number
  /** When the unspent invite bound to this account was last delivered. */
  pendingCodeSentAt: number | null
  /**
   * Whether an unused, unexpired invite code is currently bound to this mailbox.
   *
   * This is what tells the approved panel apart from a request whose code is
   * gone: without an available code the applicant has to be offered the request
   * entry point again, otherwise the page is a dead end. `pendingCodeSentAt`
   * cannot answer it, because a code whose mail failed still exists while
   * having no delivery time.
   */
  pendingCodeAvailable: boolean
  /**
   * Plaintext of the pending invite code.
   *
   * Present only in the `development` and `test` environments, where no mail is
   * delivered and the flow would otherwise be impossible to complete. Production
   * never returns it: the mail is the single delivery channel there, and no
   * administrator surface exposes it either.
   */
  developmentCode?: string
}

/* ------------------------------------------------------------------ */
/* Administration                                                      */
/* ------------------------------------------------------------------ */

/** Derived state of an invite code; the row stores timestamps, not a status. */
export type ActivationCodeState = 'unused' | 'used' | 'expired' | 'revoked'

/** One invite code as an administrator sees it. The plaintext never appears. */
export interface ActivationCodeDto {
  id: string
  /** Last four characters, for matching a row against the mail a user received. */
  codeSuffix: string
  email: string
  state: ActivationCodeState
  mailStatus: MailStatus
  mailSentAt: number | null
  note: string | null
  applicationId: string | null
  createdBy: string | null
  createdAt: number
  expiresAt: number
  usedAt: number | null
  revokedAt: number | null
}

export interface AdminApplicationDto {
  id: string
  email: string
  nickname: string | null
  reason: string | null
  status: ApplicationStatus
  decisionNote: string | null
  decidedAt: number | null
  createdAt: number
  isInvited: boolean
}

export interface AdminApplicantProfileDto {
  id: string
  createdAt: number
  loginMethods: ('email' | 'github')[]
  isInvited: boolean
  invitedAt: number | null
  bannedUntil: number | null
  referrerEmail: string | null
  referrerCode: string | null
  referralBoundAt: number | null
  referralChannel: ReferralChannel | null
}

export interface AdminApplicationDetailDto {
  application: AdminApplicationDto
  applicant: AdminApplicantProfileDto
  /** Every request this account filed, newest first. */
  history: AdminApplicationDto[]
  code: ActivationCodeDto | null
}

export interface AdminUserDto {
  id: string
  email: string
  nickname: string | null
  isInvited: boolean
  invitedAt: number | null
  bannedUntil: number | null
  banReason: string | null
  referralCode: string
  referrerEmail: string | null
  referralChannel: ReferralChannel | null
  loginMethods: ('email' | 'github')[]
  isAdmin: boolean
  createdAt: number
}

export interface AdminUserStatsDto {
  createdEvents: number
  joinedEvents: number
  referredSignups: number
}

export interface AdminUserDetailDto {
  user: AdminUserDto
  stats: AdminUserStatsDto
}

export interface AdminDto {
  id: string
  email: string
  userId: string | null
  role: AdminRole
  /** Effective set: role preset plus extras minus denials. */
  capabilities: AdminCapability[]
  capabilitiesExtra: AdminCapability[]
  capabilitiesDenied: AdminCapability[]
  status: AdminStatus
  note: string | null
  createdAt: number
  updatedAt: number
  disabledAt: number | null
}

export interface AdminSummaryDto {
  pendingApplications: number
  approvedLast7Days: number
  rejectedLast7Days: number
  availableCodes: number
  newUsersLast7Days: number
  referredSignups: number
  clicksTotal: number
  /** How far referral clicks have been folded in; `null` before the first run. */
  clicksAggregatedUntil: number | null
}

/** Map budget state as the console shows it. */
export interface AdminProviderStatusDto {
  mode: MapCallsMode
  dailyLimitPerProvider: number
  /**
   * Providers the deployment has a key for, with how many keys are configured.
   *
   * The budget table only has a row once a provider has been called, so a board
   * that listed the rows alone would show nothing on a quiet day — exactly when
   * an operator wants to know what the day's allowance is and how much of it is
   * left.
   */
  providers: Array<{ provider: string; configured: boolean }>
  budgets: Array<{
    provider: string
    service: string
    windowKind: 'day' | 'month' | 'balance'
    used: number
    limit: number
    updatedAt: number
  }>
  breakers: Array<{
    provider: string
    service: string
    windowKind: 'day' | 'month' | 'balance'
    reason: string
    openedAt: number
    resetAt: number | null
  }>
}

export interface AdminSettingsDto {
  inviteApplyEnabled: boolean
  inviteRejectCooldownDays: number
  activationCodeTtlHours: number
  /** New accounts the deployment may create per local day; zero closes registration. */
  dailyRegistrationLimit: number
  /** Paid recommendation rounds one event may ever compute. */
  recommendRoundBudgetPerEvent: number
  /** Paid rounds one account may buy for one event. */
  recommendRoundBudgetPerMember: number
  /** Paid rounds one account may buy across every event in a day. */
  recommendRoundBudgetPerAccountDaily: number
  /** Whether map vendors may be called at all, and the per-vendor daily ceiling. */
  mapCallsMode: MapCallsMode
  mapDailyBudgetPerProvider: number
}

/**
 * One row of the console's geography board.
 *
 * The engine never shows an operator which districts exist or what is stored for
 * them, so a round failing on coverage gave no way to see whether the catalog is
 * short, the reference data is missing for that district, or both. These fields
 * are exactly the ones that answer that: what the district is, how many places
 * are stored for it and for which activities, and how many anchors and derived
 * spots it has.
 */
export interface AdminDistrictStatusDto {
  id: string
  name: string
  namePinyin: string | null
  level: DistrictLevel
  /** Region a spot belongs to; null for a region. */
  parentId: string | null
  parentName: string | null
  lat: number
  lng: number
  radiusMeters: number
  address: string | null
  tags: string[]
  /** Spots derived inside this region; zero for a spot itself. */
  spotCount: number
  /** Stored places inside the district across every activity. */
  poiCount: number
  /** Activities with at least one stored place, in catalog order. */
  coveredActivities: ActivityKey[]
  /** Anchors inside the district radius. */
  anchorCount: number
}

/** Stored places of one activity across the city. */
export interface AdminActivityCoverageDto {
  activity: ActivityKey
  poiCount: number
  districtCount: number
}

/** One reference dataset as the console reports it. */
export interface AdminDatasetStatusDto {
  key: ReferenceDatasetKey
  status: ReferenceDatasetStatus
  lastRefreshedAt: number | null
  recordCount: number
  stale: boolean
  lastError: string | null
}

/**
 * The console's data and configuration board.
 *
 * Read-only. It exists so an operator can answer "is the catalog complete, and is
 * the reference data behind it actually stored" without reading the database, and
 * so an empty result has a visible cause rather than a sentence about activities.
 */
export interface AdminDataStatusDto {
  city: string
  generatedAt: number
  districts: {
    total: number
    regions: number
    spots: number
    /** Page that these items belong to, clamped to the catalog's page count. */
    page: number
    pageSize: number
    items: AdminDistrictStatusDto[]
  }
  datasets: AdminDatasetStatusDto[]
  coverage: AdminActivityCoverageDto[]
  anchors: Array<{ kind: AnchorKind; count: number }>
  counts: {
    anchors: number
    lastDepartures: number
    travelModelParams: number
    referencePois: number
    /** Scopes of a reference dataset that have been filled and marked. */
    fillMarks: number
  }
}

export interface AdminReferralDto {
  code: string
  ownerEmail: string | null
  ownerNickname: string | null
  clicks: number
  signups: number
  updatedAt: number
  lastSignupAt: number | null
}

export interface AdminReferralSignupDto {
  userId: string
  email: string
  nickname: string | null
  channel: ReferralChannel | null
  boundAt: number | null
}

/** Envelope every administrator list endpoint answers with. */
export interface PagedResponse<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
}

/** One departure-point query that found no stored anchor. */
export interface AdminLookupMissDto {
  keyword: string
  /** Whether the member asked for a nearby transit stop rather than a place. */
  withNear: boolean
  count: number
  firstAt: number
  lastAt: number
}

/** Departure-point gaps, as the console reads them. */
export interface AdminLookupMissesDto extends PagedResponse<AdminLookupMissDto> {}

/** One calibration proposal, before or after an operator applies it. */
export interface AdminCalibrationRunDto {
  id: string
  mode: string
  timeBucket: string
  before: TravelModelParamsDto
  after: TravelModelParamsDto
  maeBefore: number
  maeAfter: number
  sampleCount: number
  status: 'pending' | 'applied' | 'rejected'
  createdAt: number
  appliedAt: number | null
}

/** The coefficients one travel mode and time bucket is estimated with. */
export interface TravelModelParamsDto {
  detourFactor: number
  speedKmh: number
  overheadMinutes: number
  margin: number
}

export interface AdminCalibrationDto {
  runs: AdminCalibrationRunDto[]
}

/**
 * Why a GitHub login did not finish. The callback redirects back to the client
 * with one of these, and each maps to a message there.
 */
export const GITHUB_OAUTH_ERROR_REASONS = [
  'not_configured',
  'access_denied',
  'state_invalid',
  'exchange_failed',
  'email_unavailable',
  /**
   * The GitHub identity is valid, but the deployment has already created as many
   * accounts as its daily registration limit allows.
   */
  'registration_closed',
  'provider_error',
] as const
export type GithubOAuthErrorReason = (typeof GITHUB_OAUTH_ERROR_REASONS)[number]

/* ------------------------------------------------------------------ */
/* Events                                                              */
/* ------------------------------------------------------------------ */

export const EVENT_STATUSES = ['draft', 'collecting', 'decided', 'archived'] as const
export type EventStatus = (typeof EVENT_STATUSES)[number]

export const WEATHER_CONDITIONS = ['sunny', 'cloudy', 'rain', 'snow', 'hot', 'cold', 'unknown'] as const
export type WeatherCondition = (typeof WEATHER_CONDITIONS)[number]

export const BUDGET_LEVELS = ['low', 'medium', 'high', 'any'] as const
export type BudgetLevel = (typeof BUDGET_LEVELS)[number]

export interface EventSummaryDto {
  id: string
  title: string
  city: CityKey
  eventDateStart: string
  eventDateEnd: string | null
  activities: ActivityKey[]
  /**
   * Declared activities the group treats as nice to have.
   *
   * The rest are required: a candidate district must have places for them, and
   * the time window must fit them. An optional activity only affects scoring and
   * the notes, so one member's wish cannot veto every district.
   */
  optionalActivities: ActivityKey[]
  weatherCondition: WeatherCondition
  aggregationPolicy: AggregationPolicy
  needProfile: NeedProfile
  status: EventStatus
  shareToken: string
  creatorId: string
  creatorNickname: string | null
  /** Registrations in the event. */
  unitCount: number
  /** People those registrations stand for. */
  peopleCount: number
  roundCount: number
  createdAt: number
  updatedAt: number
}

export interface EventDetailDto extends EventSummaryDto {
  /**
   * Roster rows as the viewer may read them.
   *
   * The creator receives `ParticipantDto`; everybody else receives the shared
   * projection, which drops the note, the budget and the account id.
   */
  participants: Array<ParticipantDto | PublicParticipantDto>
  rounds: RoundSummaryDto[]
  /**
   * How many paid rounds the caller and the event have left.
   *
   * Rounds are counted, not clicks: answering a repeat of unchanged inputs from
   * the stored round costs nothing and consumes nothing.
   */
  recommendationBudget: RecommendationBudgetDto
}

/**
 * Round budget as the interface reports it.
 *
 * Three ceilings apply at once and the smallest one decides, so the client shows
 * each of them rather than a single number it would have to guess at.
 */
export interface RecommendationBudgetDto {
  eventLimit: number
  eventUsed: number
  /** Paid rounds one account may buy for this event. */
  memberLimit: number
  memberUsed: number
  /** Paid rounds one account may buy across every event in a day. */
  accountDailyLimit: number
  accountDailyUsed: number
  /** Smallest remaining value across the three ceilings. */
  remaining: number
}

export interface EventListResponse {
  created: EventSummaryDto[]
  joined: EventSummaryDto[]
}

export interface EventMutationResponse {
  event: EventDetailDto
}

export interface CreateEventInput {
  title: string
  city: CityKey
  eventDateStart: string
  eventDateEnd?: string | null
  activities: ActivityKey[]
  optionalActivities?: ActivityKey[]
  weatherCondition?: WeatherCondition
  /** How a round balances the group; the engine defaults to least misery. */
  aggregationPolicy?: AggregationPolicy
  /** Whether the answer must contain a mall, or only somewhere to eat. */
  needProfile?: NeedProfile
}

export interface UpdateEventInput extends Partial<CreateEventInput> {
  status?: EventStatus
}

/* ------------------------------------------------------------------ */
/* Participants                                                        */
/* ------------------------------------------------------------------ */

/** Participant data visible to the event creator. */
export interface ParticipantDto {
  id: string
  eventId: string
  userId: string
  nickname: string
  /** People this registration stands for. */
  headcount: number
  /** Travel group this registration departs with, once the join flow assigned one. */
  travelGroupId: string | null
  /** Name of the departure point, which belongs to the group. */
  departureLabel: string | null
  modes: TravelMode[]
  attendingActivities: ActivityKey[]
  availableTimeStart: string | null
  availableTimeEnd: string | null
  maxTravelMinutes: number
  budget: BudgetLevel
  note: string | null
  /**
   * Requirements the member stated, visible to the event and to anyone holding the
   * share link: the group is deciding together, and "who cannot eat what" is part of
   * the decision.
   */
  constraints: ParticipantConstraints | null
  /** Brands, venues or cuisines the member asked for. */
  wants: string[] | null
  /** Whether the member solves a too-long last mile themselves. */
  selfArrange: SelfArrangePreference | null
  joinedAt: number
}

/**
 * Participant data a signed-in holder of the share link may read: identity and
 * constraints, never exact coordinates.
 *
 * The list is not anonymous: `GET /api/shared/:shareToken/participants` needs a
 * session, and only the overview and the stored rounds are readable without one.
 */
export interface PublicParticipantDto {
  id: string
  nickname: string
  /** People this registration stands for, so a list can show the party size. */
  headcount: number
  /** Name of the departure point the registration travels from. */
  departureLabel: string | null
  modes: TravelMode[]
  attendingActivities: ActivityKey[]
  availableTimeStart: string | null
  availableTimeEnd: string | null
  maxTravelMinutes: number
  joinedAt: number
}

export interface ParticipantInput {
  nickname: string
  /**
   * People this registration stands for.
   *
   * One account may register a household, so a registration is a booking unit
   * rather than a person. Defaults to one.
   */
  headcount?: number
  modes: TravelMode[]
  attendingActivities: ActivityKey[]
  availableTimeStart?: string | null
  availableTimeEnd?: string | null
  maxTravelMinutes?: number
  /**
   * Requirements the member will not compromise on, all optional.
   *
   * The form lets a member skip every field; an absent value and an empty list both
   * mean "no special requirement", and neither is treated as a constraint.
   */
  constraints?: ParticipantConstraints | null
  /** Brands, venues or cuisines the member asked for. */
  wants?: string[] | null
  /** Whether the member solves a too-long commute themselves, and up to what cost. */
  selfArrange?: SelfArrangePreference | null
  budget?: BudgetLevel
  note?: string | null
}

export interface ParticipantResponse {
  participant: ParticipantDto
}

/** A departure point as the picker shows it. */
export interface DepartureAnchorDto {
  id: string
  kind: AnchorKind
  name: string
  lat: number
  lng: number
  source: AnchorSource
}

export interface DepartureAnchorSearchResponse {
  anchors: DepartureAnchorDto[]
  /** The caller's own recent points, which are worth offering before anything else. */
  remembered: DepartureAnchorDto[]
}

export interface DepartureAnchorLookupResponse {
  anchors: DepartureAnchorDto[]
}

/** One member of a travel group, as the departure list shows them. */
export interface DepartureMemberDto {
  id: string
  nickname: string
  headcount: number
}

/**
 * A travel group with its departure point.
 *
 * Readable by members only: a departure point may be a private address that a
 * member chose, so it is not part of the anonymous share view.
 */
export interface DepartureDto {
  id: string
  label: string
  anchorName: string
  lat: number
  lng: number
  /** People the group stands for. */
  headcount: number
  members: DepartureMemberDto[]
}

/** How close an event is to the departure ceiling. */
export interface DeparturePressureDto {
  count: number
  limit: number
  warningThreshold: number
  warned: boolean
  full: boolean
}

export interface DeparturesResponse {
  departures: DepartureDto[]
  pressure: DeparturePressureDto
  /** The caller's group, when they have registered. */
  myGroupId: string | null
  /** Whether the caller has already dismissed the approach warning. */
  warningDismissed: boolean
}

/**
 * One departure point as a joining member sees it.
 *
 * The join wizard runs before membership exists, so it gets names and sizes
 * rather than the full list: enough to choose whom to travel with, without
 * disclosing any member's address to a signed-in visitor holding the link.
 */
export interface DepartureOptionDto {
  id: string
  label: string
  headcount: number
}

export interface DepartureOptionsResponse {
  options: DepartureOptionDto[]
}

/**
 * What the join wizard sends.
 *
 * A departure point may be named three ways — an anchor the picker resolved, free
 * text, or a raw position — and the server turns all three into the same stored
 * anchor. Sending a position is the escape hatch for a member who wants their
 * exact spot rather than the nearest named place.
 */
export interface JoinEventInput extends ParticipantInput {
  anchorId?: string | null
  anchorQuery?: string | null
  locationLat?: number
  locationLng?: number
  locationLabel?: string | null
  /** Existing travel group the member asked to join instead of starting one. */
  travelGroupId?: string | null
}

export interface PublicParticipantsResponse {
  participants: PublicParticipantDto[]
}

/* ------------------------------------------------------------------ */
/* Recommendations                                                     */
/* ------------------------------------------------------------------ */

export const ROUND_STATUSES = ['open', 'decided'] as const
export type RoundStatus = (typeof ROUND_STATUSES)[number]

/**
 * Execution state of the background pipeline.
 *
 * Kept apart from `RoundStatus`, which is the decision lifecycle the vote page and
 * the organizer gate read: a round is `open` while it collects ballots and
 * `decided` once one is locked in, whether or not the pipeline that produced it has
 * finished. A running round is pollable from the moment it is queued.
 */
export const ROUND_RUN_STATUSES = [
  'queued',
  'preparing',
  'screening',
  'routing',
  'scoring',
  'ready',
  'failed',
] as const
export type RoundRunStatus = (typeof ROUND_RUN_STATUSES)[number]

/** Why a round stopped without producing candidates. Bounded so the UI can translate it. */
export const ROUND_FAILURE_REASONS = [
  'provider_unavailable',
  'timeout',
  'budget_exhausted',
  'internal',
] as const
export type RoundFailureReason = (typeof ROUND_FAILURE_REASONS)[number]

/**
 * Why a round's data is thinner than it should be.
 *
 * `none` is the ordinary case; the other values say which budget ran out, so the
 * interface can promise when the situation recovers instead of guessing.
 */
export const DATA_DEGRADED_REASONS = [
  'none',
  'quota_daily',
  'quota_month',
  'quota_balance',
  'provider_down',
  'readonly',
] as const
export type DataDegradedReason = (typeof DATA_DEGRADED_REASONS)[number]

/** Capabilities a degraded round could not refresh. */
export const DEGRADED_CAPABILITIES = ['poi', 'route', 'last_transit'] as const
export type DegradedCapability = (typeof DEGRADED_CAPABILITIES)[number]

/** Progress of one running round: a real step counter, never a time estimate. */
export interface RoundProgress {
  stage: RoundRunStatus
  done: number
  total: number
  /** Translation key the interface renders next to the counter. */
  label: string
  updatedAt: number
}

/**
 * Steps a round has completed, which is what makes a resumed run idempotent.
 *
 * The names are the pipeline stages, in the order the engine runs them, plus one
 * name per district the journey-verification batch finished (`route:<districtId>`).
 * A name already in `done` is not recomputed: the coarse stages read from the
 * database, and a verified district keeps its position in a batch that is
 * otherwise sorted by estimate, so the next attempt stops at the same candidate
 * the first one would have. `current` is the stage being worked on, which is what
 * the reader polls while the round runs.
 */
export interface RoundSteps {
  done: string[]
  current: string | null
}

/** Per stage wall-clock milliseconds, so "slow" can be attributed. */
export interface RoundStageTimings {
  read: number
  prepare: number
  coverage: number
  routing: number
  scoring: number
  persist: number
}

/** Bounded diagnostics recorded with every round. */
export interface RoundDiagnostics {
  scoringSource: 'jev' | 'fallback'
  notes: string[]
  payloadChars: number
  inputTokens: number | null
  outputTokens: number | null
  routeCalls: number
  coverageReads: number
  estimateMae: number | null
  stageMs: RoundStageTimings
  /** Candidates the route budget could not pay for. */
  skippedByBudget: number
  /**
   * Districts whose verification a previous attempt had already finished.
   *
   * Recorded so a resumed round says what it carried over instead of looking
   * like a first attempt that happened to be cheap.
   */
  reusedSteps: number
}

/** How the group's ballots related to the engine ranking at the moment of the decision. */
export interface RoundTallySnapshot {
  decidedRecommendationId: string
  /** Candidate ids in Borda order, best first. */
  bordaOrder: string[]
  /** Whether the engine's rank 1 was the candidate the organizer locked in. */
  agreed: boolean
  /** Rank the decided candidate held in the engine ranking, 1-based. */
  engineRankOfDecided: number | null
}

/**
 * How a round balances the group: the least-misery default minimises the worst
 * sacrifice, `average` minimises the total, `most_pleasure` maximises how many
 * members are satisfied.
 */
export const AGGREGATION_POLICIES = ['least_misery', 'average', 'most_pleasure'] as const
export type AggregationPolicy = (typeof AGGREGATION_POLICIES)[number]

/** What the group needs: a full outing, or just a meal somewhere in an area. */
export const NEED_PROFILES = ['general', 'meal_only'] as const
export type NeedProfile = (typeof NEED_PROFILES)[number]

/** Whether the organizer typed the weather or a forecast filled it in. */
export const WEATHER_SOURCES = ['manual', 'forecast'] as const
export type WeatherSource = (typeof WEATHER_SOURCES)[number]

/** Kind of participation one departure group gets in a candidate plan. */
export const PARTICIPATION_KINDS = ['full', 'lateJoin', 'earlyLeave', 'selfArrange'] as const
export type ParticipationKind = (typeof PARTICIPATION_KINDS)[number]

/** One departure group's place in a candidate's plan. */
export interface ParticipationEntry {
  unitId: string
  label: string
  kind: ParticipationKind
  /** Local `HH:mm` the group joins, when it cannot attend from the start. */
  joinAt: string | null
  /** Local `HH:mm` the group leaves, when it cannot stay to the end. */
  leaveAt: string | null
  /** Minutes lost to a late join or an early leave. */
  lostMinutes: number
  /** Estimated taxi fare in yuan when the group solves the last mile itself. */
  taxiYuan: number | null
  /** This group's extra minutes over the best candidate, which is what regret is built from. */
  regretMinutes: number
}

/** One member's constraint or wish, as the satisfaction check resolved it. */
export interface SatisfactionEntry {
  memberId: string
  nickname: string
  /** The constraint or wish, as it reads in the interface (`no_spicy`, a brand, ...). */
  need: string
  /** A safety requirement that nothing satisfies is reported separately from a wish. */
  required: boolean
  ok: boolean
}

/** How many members a candidate can actually serve. */
export interface CandidateSatisfaction {
  satisfiedCount: number
  total: number
  entries: SatisfactionEntry[]
}

/** One activity's slot in a candidate's plan. */
export interface ScheduleEntry {
  activity: ActivityKey
  /** Local `HH:mm` the activity starts. */
  startAt: string
  /** Local `HH:mm` the activity ends. */
  endAt: string
}

/** Per member constraints collected on the join form, all optional. */
export interface ParticipantConstraints {
  avoid: string[]
  allergy: string[]
  require: string[]
}

/** Whether a member will solve a too-long commute themselves, and up to what cost. */
export interface SelfArrangePreference {
  allowed: boolean
  maxExtraMinutes: number | null
  maxYuan: number | null
}


/**
 * Catalog granularity.
 *
 * A region is one of the curated commercial districts the coarse screen ranks; a
 * spot is a single mall or commercial street inside one. The screen runs over
 * regions first, because that is the cheap comparison, and refinements are only
 * considered inside the regions that survive.
 */
export const DISTRICT_LEVELS = ['region', 'spot'] as const
export type DistrictLevel = (typeof DISTRICT_LEVELS)[number]

export interface PoiCoverageEntry {
  activity: ActivityKey
  /**
   * Whether the activity is a requirement rather than a wish.
   *
   * A required activity outranks every optional one when the round has to drop
   * something, but it is not a veto: a district that covers no required activity
   * is still a candidate when it covers an optional one, because a partial plan
   * is better than no plan at all.
   */
  required: boolean
  /** People whose registration asked for this activity. */
  demand: number
  poiCount: number
  /**
   * Whether the plan actually schedules this activity.
   *
   * False means the district has no place for it, or the group's window cannot
   * fit it, and the round dropped it. The counts are kept either way, so the
   * interface can show what was available next to what was left out.
   */
  delivered: boolean
  samplePois: Array<{ id: string; name: string; distanceMeters: number }>
}

/**
 * One departure point's journey to a candidate.
 *
 * Travel is computed per departure point rather than per person: the people who
 * start from one place arrive together, so the plan states one journey and the
 * number of people it covers.
 */
export interface TravelTimeEntry {
  /** Travel group the journey belongs to. */
  departureId: string
  /** Name of the group, which is also its departure point. */
  label: string
  /** People behind that departure point. */
  headcount: number
  minutes: number
  mode: TravelMode
  distanceMeters: number | null
  /**
   * Limit the traveller themselves declared.
   *
   * An organizer may relax the limits for one round; keeping the declared value
   * next to the measurement is what lets the interface say who is over their own
   * limit by how much, instead of hiding the relaxation.
   */
  declaredLimitMinutes: number
  /**
   * Estimate the measured journey was screened against.
   *
   * Kept so the two numbers can be told apart wherever a journey is shown, and so
   * calibration can compare what the model promised with what the router answered.
   */
  estimatedMinutes: number | null
}

export interface TravelStats {
  maxMinutes: number
  /** Average over people rather than over departure points. */
  averageMinutes: number
  /** Spread across departure points. */
  spreadMinutes: number
  /** Departure point whose journey is the longest, when one is. */
  limitingDepartureId: string | null
}

/**
 * Reference data a round read, with the date it was refreshed on.
 *
 * Stored with the round because a plan is only as current as the data behind it,
 * and the interface has to be able to say how current that was.
 */
export interface DatasetFreshnessRecord {
  key: ReferenceDatasetKey
  status: ReferenceDatasetStatus
  lastRefreshedAt: number | null
  recordCount: number
}

/**
 * The departure points a round was computed from.
 *
 * Stored with the round so a later merge cannot change what an already decided
 * plan meant.
 */
export interface TravelGroupSnapshot {
  id: string
  label: string
  anchorName: string
  /**
   * Coordinates of the departure point. Absent from the share-link projection,
   * which is served to anybody holding the link; only the creator and the joined
   * members receive them.
   */
  lat?: number | null
  lng?: number | null
  headcount: number
  members: Array<{ id: string; nickname: string; headcount: number }>
}

export interface RecommendationDetails {
  district: {
    id: string
    name: string
    namePinyin: string | null
    /** Candidates come from the curated district catalog. */
    source: 'catalog'
    /** Whether this candidate is a region or a spot inside one. */
    level: DistrictLevel
    /** Region a spot belongs to; null for a region. */
    parentName: string | null
    address: string | null
    tags: string[]
    parkingInfo: string | null
  }
  poiCoverage: PoiCoverageEntry[]
  /**
   * What the round actually schedules here, and what it had to drop.
   *
   * The engine degrades instead of refusing when a district cannot deliver every
   * declared activity: it keeps the highest priority subset that both has a place
   * and fits the group's window. This field is that decision stated openly, so the
   * interface can warn about it rather than presenting a partial plan as complete.
   */
  plan: RecommendationPlan
  travelTimes: TravelTimeEntry[]
  travelStats: TravelStats
  lastTransit: {
    checked: boolean
    ok: boolean
    /** Latest usable departure from the station near the venue, `HH:mm`. */
    lastDeparture: string | null
    /** Station the bound belongs to, when the check ran. */
    anchorName: string | null
    /** Always true: the bound is empirical and has to be confirmed. */
    estimated: boolean
  }
  timeWindow: {
    /** The window fits every activity in the plan. */
    ok: boolean
    requiredMinutes: number
    availableMinutes: number | null
    /** The window also fits every declared activity, optional ones included. */
    optionalFits: boolean
  }
  weather: { condition: WeatherCondition; outdoorActivitiesAffected: ActivityKey[] }
  scoring: {
    source: 'jev' | 'fallback'
    /** Weighted total of the dimension scores, less any weather deduction. */
    score: number
    /** Per dimension scores the total was computed from, each 0-10. */
    dimensions: DimensionScores
    confidence: number | null
    needsReview: boolean
    /** Probability the decision model assigned to this candidate being first. */
    primaryProbability: number | null
  }
  hardConstraintsPassed: string[]
  /** When each activity of the plan runs, once the round assigns times. */
  schedule: ScheduleEntry[]
  /** What each departure group has to give up to take part in this candidate. */
  participation: ParticipationEntry[]
  /** Whose constraints and wishes this candidate can serve. */
  satisfaction: CandidateSatisfaction
  /** Worst sacrifice any group pays here, in minutes; the least-misery key. */
  worstRegretMinutes: number
}

/**
 * What one candidate's round actually delivers.
 *
 * Ordered by priority, so the first entry is the activity the organizers required
 * most or the most people asked for. `droppedActivities` is empty when the plan
 * delivers everything declared, which is the ordinary case; a non-empty list means
 * the round was automatically degraded and has to say so.
 */
export interface RecommendationPlan {
  activities: ActivityKey[]
  droppedActivities: ActivityKey[]
  degraded: boolean
}

export interface RecommendationDto {
  id: string
  eventId: string
  round: number
  /** Position in the ranking the interface shows, with the model's first choice promoted. */
  rank: number
  /** Position the mechanical ordering gave it, before any promotion. */
  engineRank: number
  districtName: string
  districtPoiId: string | null
  districtLat: number | null
  districtLng: number | null
  /** Where the address came from: the curated catalog, or a cluster inside a region. */
  source: 'catalog' | 'mealArea'
  /** Name of the restaurant cluster when the candidate is a meal area. */
  clusterName: string | null
  /** Restaurants the cluster holds, when the candidate is a meal area. */
  restaurantCount: number | null
  score: number
  details: RecommendationDetails
  createdAt: number
}

export interface RoundSummaryDto {
  round: number
  status: RoundStatus
  /** Execution state of the pipeline that produced (or is producing) this round. */
  runStatus: RoundRunStatus
  /** Real step counter while the round runs; null once it is ready or failed. */
  progress: RoundProgress | null
  /** Whether the round ran with thinner data than it wanted, and why. */
  dataDegraded: DataDegradedReason
  /** Whether the organizer or an ordinary member started the round. */
  triggerSource: RecommendationTriggerSource
  /** Registration units the round was computed for. */
  unitCount: number
  /** People those units stood for, which is what the plan is sized for. */
  peopleCount: number
  voteCount: number
  primary: RecommendationDto | null
  /**
   * True when the leading candidate could not deliver every declared activity.
   *
   * The list needs the warning without loading a round, because a degraded plan is
   * a plan the group still has to agree to; the per activity detail is on the
   * round itself.
   */
  degraded: boolean
  /** Map implementation used for this round's travel times. */
  /**
   * Providers available to the round, joined with `+`, or `offline` when the
   * deployment has no map key. Clients split on `+` to detect synthesized data
   * and to render the attribution the vendors require.
   */
  mapProvider: string | null
  createdAt: number
  /**
   * When the round stopped running, or null while it still is.
   *
   * The client uses the difference between this and `createdAt` to decide
   * whether the next run should be queued instead of waited for: the first slow
   * round is the evidence that this event's rounds are slow.
   */
  finishedAt: number | null
  /** Why the round failed, when it did; the list states it without opening the round. */
  failureReason: RoundFailureReason | null
}

export interface RoundDetailDto extends RoundSummaryDto {
  alternates: RecommendationDto[]
  /**
   * True when the first choice's scoring was flagged for manual review.
   *
   * It follows the candidate the round proposes rather than every candidate it
   * lists: the notice is about the recommendation, and one unsure alternate used
   * to make the whole round read as low confidence. Each candidate keeps its own
   * flag in its details.
   */
  needsReview: boolean
  decidedRecommendationId: string | null
  /** Capabilities the round could not use because of a quota or provider problem. */
  degradedCapabilities: DegradedCapability[]
  /** Bounded diagnostics: stage timings, calls, tokens, notes. */
  diagnostics: RoundDiagnostics | null
  /** Departure points the round was computed from, as they were then. */
  travelGroups: TravelGroupSnapshot[]
  /** Reference data the round read, and how current it was. */
  dataFreshness: DatasetFreshnessRecord[]
}

export interface RoundsResponse {
  rounds: RoundSummaryDto[]
}

export interface RoundDetailResponse {
  round: RoundDetailDto
}

export interface RunRecommendationResponse {
  round: RoundDetailDto
  /**
   * True when the round was queued rather than finished.
   *
   * The caller navigates to the round and polls it; the round itself carries the
   * progress counter and the failure reason.
   */
  pending?: boolean
  /**
   * True when the run returned an existing round because nothing that decides a
   * plan had changed, instead of spending the providers again.
   */
  reused: boolean
}

/* ------------------------------------------------------------------ */
/* Votes                                                              */
/* ------------------------------------------------------------------ */

export interface VoteInput {
  /** Recommendation ids ordered from most to least preferred. */
  rankings: string[]
}

/**
 * One candidate's aggregate.
 *
 * Two readings of the same ballots are reported: the unit figures say how many
 * registrations voted, the weighted ones say how many people they stand for. The
 * weighted figures are what the ranking uses.
 */
export interface VoteBreakdownEntry {
  recommendationId: string
  districtName: string
  /** First preference count. */
  /** Ballots that ranked this candidate first. */
  firstChoiceCount: number
  /** People behind those ballots. */
  firstChoiceWeight: number
  /** Borda points accumulated across all ballots. */
  bordaPoints: number
  /** Average rank across all ballots, `null` when never ranked. */
  averageRank: number | null
}

export interface VoteResultDto {
  round: number
  /** Ballots cast, which equals the number of voting registration units. */
  voteCount: number
  /** Registrations in the event. */
  unitCount: number
  /** People those registrations stand for. */
  peopleCount: number
  /** People behind the ballots cast so far. */
  votedPeopleCount: number
  breakdown: VoteBreakdownEntry[]
  /** Recommendation ids ordered by the aggregate result. */
  ranking: string[]
  myRankings: string[] | null
}

/* ------------------------------------------------------------------ */
/* Comments                                                           */
/* ------------------------------------------------------------------ */

export interface CommentDto {
  id: string
  eventId: string
  participantId: string
  nickname: string
  content: string
  replyToId: string | null
  replyToNickname: string | null
  mentionedNames: string[]
  createdAt: number
}

export interface CommentsResponse {
  comments: CommentDto[]
  /** Cursor for the next incremental poll, equal to the newest `createdAt`. */
  cursor: number | null
}

/* ------------------------------------------------------------------ */
/* Shared (share-token scoped) responses                              */
/* ------------------------------------------------------------------ */

export interface PublicEventResponse {
  event: {
    /** Event id, needed by the organizer for creator-only actions. */
    id: string
    title: string
    city: CityKey
    eventDateStart: string
    eventDateEnd: string | null
    activities: ActivityKey[]
    optionalActivities: ActivityKey[]
    weatherCondition: WeatherCondition
    status: EventStatus
    shareToken: string
    organizerNickname: string | null
    /** Registrations in the event. */
    unitCount: number
    /** People those registrations stand for. */
    peopleCount: number
    createdAt: number
    /** Budget for the signed-in member; null for an anonymous visitor. */
    recommendationBudget: RecommendationBudgetDto | null
  }
  /** Whether the caller is signed in. */
  viewer: {
    isAuthenticated: boolean
    isJoined: boolean
    isOrganizer: boolean
    participantId: string | null
  }
}

/* ------------------------------------------------------------------ */
/* Calendar                                                           */
/* ------------------------------------------------------------------ */

export const CALENDAR_ENTRY_KINDS = ['activity', 'travel', 'buffer'] as const
export type CalendarEntryKind = (typeof CALENDAR_ENTRY_KINDS)[number]

export interface CalendarTimelineEntryDto {
  key: string
  kind: CalendarEntryKind
  /** Translation key the client resolves, for example `activity.dining`. */
  labelKey: string
  /** Local date-time, matching the event's own time representation. */
  startAt: string
  endAt: string
  durationMinutes: number
}

export interface CalendarViewResponse {
  event: PublicEventResponse['event']
  /** IANA time zone the timeline is expressed in. */
  timeZone: string
  /**
   * The locked-in district, when the organizer has decided a round.
   *
   * `activities` is the plan's own list, which may be shorter than the event's
   * declaration: the group locked in a plan that dropped whatever did not fit, and
   * the timeline is built from this rather than from what was originally wished.
   */
  decided: {
    recommendationId: string
    districtName: string
    activities: ActivityKey[] | null
  } | null
  timeline: CalendarTimelineEntryDto[]
  /** Path of the downloadable ICS file, share token included. */
  icsPath: string
}

/* ------------------------------------------------------------------ */
/* Weather                                                            */
/* ------------------------------------------------------------------ */

export interface WeatherSuggestionResponse {
  city: CityKey
  date: string
  condition: WeatherCondition
  text: string | null
  temperatureCelsius: number | null
  /** Always true: the user's manual input wins over this suggestion. */
  advisory: true
}

/* ------------------------------------------------------------------ */
/* Infrastructure                                                     */
/* ------------------------------------------------------------------ */

export interface HealthResponse {
  ok: boolean
  /**
   * Environment name and provider inventory.
   *
   * Both are present only in local-like environments: a probe in production must
   * not tell an anonymous caller which third parties are configured, so the
   * production payload carries `ok`, `database`, `schema` and `timestamp` alone.
   */
  environment?: string
  database: 'ok' | 'unavailable'
  /** Whether the tables this Worker needs are present. */
  schema: 'ok' | 'missing'
  /** Which third party implementations are active, for operators. */
  providers?: {
    /** Configured providers joined with `+`, or `offline`. */
    map: string
    mapLive: boolean
    decision: string
    decisionLive: boolean
    email: 'resend' | 'development'
  }
  timestamp: number
}

export interface ApiErrorResponse {
  error: {
    code: ApiErrorCode
    message: string
    details?: Record<string, unknown>
  }
}
